import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useApiCall } from '../hooks/useApiCall';
import { useToast } from '../contexts/ToastContext';

interface SidebarProps {
  onHoverChange?: (isHovered: boolean) => void;
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onHoverChange, onLogout }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState<'info' | 'username' | 'password'>('info');
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  
  // Username change states
  const [currentPasswordForUsername, setCurrentPasswordForUsername] = useState('');
  const [newUsername, setNewUsername] = useState('');
  
  // Password change states
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  
  // Password visibility states
  const [showCurrentPasswordForUsername, setShowCurrentPasswordForUsername] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const { toggleTheme } = useTheme();
  const { user, canAccessAdminPanel, canAccessTranslateAdminPanel, canAccessFRUpdateAdminPanel } = useAuth();
  const { apiCall } = useApiCall();
  const { error: showError, success: showSuccess } = useToast();
  
  // Debug: Check admin panel access
  React.useEffect(() => {
    console.log('🎯 Sidebar Admin Panel Access Check:', {
      user: user,
      canAccessTranslateAdminPanel: canAccessTranslateAdminPanel(),
      canAccessFRUpdateAdminPanel: canAccessFRUpdateAdminPanel()
    });
  }, [user]);
  
  // Mevcut sayfayı URL'den belirle
  const activeSection = location.pathname.substring(1) || 'home';

  // Rol etiketlerini Türkçe'ye çevir
  const getRoleLabel = (role?: string): string => {
    if (!role) return 'Kullanıcı';
    
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Yönetici';
      case 'editor':
        return 'Editör';
      case 'redactor':
        return 'Redaktör';
      case 'writer':
        return 'Yazar';
      default:
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  // Uygulama rollerini formatla
  const formatAppRoles = (): string => {
    if (!user?.app_roles) return 'Kullanıcı';
    
    const roles: string[] = [];
    
    if (user.app_roles.translate) {
      roles.push(`Çevirmen: ${getRoleLabel(user.app_roles.translate)}`);
    }
    
    if (user.app_roles.fr_update) {
      roles.push(`İçerik Güncelleme: ${getRoleLabel(user.app_roles.fr_update)}`);
    }
    
    return roles.length > 0 ? roles.join(' | ') : 'Kullanıcı';
  };

  // No need for auto-expand since translator is no longer expandable

  const handleMouseEnter = () => {
    setIsHovered(true);
    onHoverChange?.(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    onHoverChange?.(false);
  };

  // Mobilde route değişince sidebar'ı kapat
  React.useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Reset modal states when opening
  const handleOpenProfileModal = () => {
    setIsProfileModalOpen(true);
    setActiveProfileTab('info');
    setCurrentPasswordForUsername('');
    setNewUsername('');
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setShowCurrentPasswordForUsername(false);
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
  };

  // Handle username change
  const handleChangeUsername = async () => {
    if (!newUsername.trim()) {
      showError('Hata', 'Yeni kullanıcı adı boş olamaz');
      return;
    }
    
    if (!currentPasswordForUsername.trim()) {
      showError('Hata', 'Şifrenizi girmelisiniz');
      return;
    }

    if (newUsername === user?.name) {
      showError('Hata', 'Yeni kullanıcı adı mevcut kullanıcı adınız ile aynı olamaz');
      return;
    }

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/change_username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user?.id,
          password: currentPasswordForUsername,
          username: newUsername
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    }, {
      loadingKey: 'change-username',
      loadingMessage: 'Kullanıcı adı değiştiriliyor...',
      errorMessage: 'Kullanıcı adı değiştirilirken hata oluştu',
      showToast: false
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      showSuccess('Başarılı', 'Kullanıcı adınız başarıyla değiştirildi. Lütfen tekrar giriş yapın.');
      setCurrentPasswordForUsername('');
      setNewUsername('');
      // Logout after username change
      setTimeout(() => {
        if (onLogout) {
          setIsProfileModalOpen(false);
          onLogout();
        }
      }, 2000);
    } else {
      showError('Hata', 'Kullanıcı adı değiştirilemedi. Lütfen şifrenizi kontrol edin.');
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (!oldPassword.trim()) {
      showError('Hata', 'Eski şifrenizi girmelisiniz');
      return;
    }

    if (!newPassword.trim()) {
      showError('Hata', 'Yeni şifre boş olamaz');
      return;
    }

    if (newPassword.length < 6) {
      showError('Hata', 'Yeni şifre en az 6 karakter olmalıdır');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      showError('Hata', 'Yeni şifreler eşleşmiyor');
      return;
    }

    if (oldPassword === newPassword) {
      showError('Hata', 'Yeni şifre eski şifre ile aynı olamaz');
      return;
    }

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/change_password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user?.id,
          old_password: oldPassword,
          new_password: newPassword
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    }, {
      loadingKey: 'change-password',
      loadingMessage: 'Şifre değiştiriliyor...',
      errorMessage: 'Şifre değiştirilirken hata oluştu',
      showToast: false
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      showSuccess('Başarılı', 'Şifreniz başarıyla değiştirildi. Lütfen tekrar giriş yapın.');
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Logout after password change
      setTimeout(() => {
        if (onLogout) {
          setIsProfileModalOpen(false);
          onLogout();
        }
      }, 2000);
    } else {
      showError('Hata', 'Şifre değiştirilemedi. Lütfen eski şifrenizi kontrol edin.');
    }
  };

  const menuItems = [
    {
      id: 'home',
      label: 'Ana Sayfa',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <path d="M3 9.5L12 3l9 6.5v11a1 1 0 01-1 1H4a1 1 0 01-1-1v-11z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      id: 'translator',
      label: 'Çevirmen',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <path d="M5 8l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 14l6-6 2-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 5h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 2h1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 22l-5-10-5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 18h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      id: 'fr-update',
      label: 'İçerik Güncelleme',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15 3v4a2 2 0 002 2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    }
  ];

  return (
    <>
      {/* Mobil üst header bar */}
      <div className="app-mobile-header">
        <div className="app-mobile-header-brand">
          <img src="/finar_ai_logo.png" alt="Finar AI" className="app-mobile-header-logo" />
          <span className="app-mobile-header-title">Finar AI</span>
        </div>
        <button
          className="app-sidebar-mobile-toggle"
          onClick={() => setIsMobileOpen(true)}
          aria-label="Menüyü Aç"
        >
          <svg className="w-5 h-5 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Backdrop — mobil drawer açıkken */}
      {isMobileOpen && (
        <div
          className="app-sidebar-backdrop"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

    <div
      className={`app-sidebar fixed left-8 top-8 bottom-8 shadow-2xl transition-all duration-300 ease-in-out z-[100] rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 ${
        isHovered ? 'w-80' : 'w-20'
      }${isMobileOpen ? ' mobile-open' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo/Brand Area */}
      <div className="h-20 flex items-center border-b border-gray-200 dark:border-gray-700">
        {/* Logo Image Container - Fixed position, never moves */}
        <div className="w-20 flex items-center justify-center flex-shrink-0">
          <img 
            src="/finar_ai_logo.png" 
            alt="Finar Translate Logo" 
            className="w-16 h-16 object-contain"
          />
        </div>
        
        {/* Brand Text - Only visible when expanded */}
        <div className={`transition-all duration-300 overflow-hidden ${
          isHovered ? 'opacity-100 w-auto' : 'opacity-0 w-0'
        }`}>
          <h2 
            className="text-gray-800 dark:text-white font-bold text-xl whitespace-nowrap tracking-wide"
            style={{ 
              fontFamily: "'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: '700',
              letterSpacing: '0.05em'
            }}
          >
            Finar AI
          </h2>
        </div>

        {/* Mobil kapatma butonu — sadece mobil drawer açıkken görünür */}
        <button
          className="app-sidebar-close-btn ml-auto mr-3 w-9 h-9 flex items-center justify-center rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
          onClick={() => setIsMobileOpen(false)}
          aria-label="Menüyü Kapat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="app-sidebar-nav flex-1 py-8">
        <div className="px-4 space-y-4">
          {menuItems.map((item) => (
            <div key={item.id}>
              {/* Ana Menü Butonu */}
              <button
                onClick={() => navigate(`/${item.id}`)}
                className={`w-full h-12 flex items-center justify-start transition-all duration-200 group relative rounded-lg ${
                  activeSection === item.id
                    ? 'bg-[#02c2ff] text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
                }`}
              >
                {/* Icon - Perfectly Centered */}
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                  <div className={`${
                    activeSection === item.id
                      ? 'text-white' 
                      : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'
                  }`}>
                    {React.cloneElement(item.icon, { 
                      className: 'w-5 h-5',
                      strokeWidth: activeSection === item.id ? 2.2 : 1.8
                    })}
                  </div>
                </div>
                
                {/* Label - Only visible when expanded */}
                <div className={`flex items-center justify-between flex-1 transition-all duration-300 overflow-hidden ${
                  isHovered ? 'opacity-100 w-auto' : 'opacity-0 w-0'
                }`}>
                  <span className="font-medium whitespace-nowrap text-base">
                    {item.label}
                  </span>
                </div>
              </button>

              {/* Alt menü artık yok çünkü translator expandable değil */}
            </div>
          ))}
        </div>
      </nav>

      {/* Bottom Controls - Fixed at very bottom */}
      <div className="app-sidebar-bottom absolute bottom-0 left-0 right-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-b-2xl">
        <div className="px-4 py-3 space-y-2">

          {/* Admin Panel Button */}
          {(canAccessTranslateAdminPanel() || canAccessFRUpdateAdminPanel()) && (
            <div className="relative">
              {isAdminMenuOpen && (
                <>
                  <div className="fixed inset-0 z-[90]" onClick={() => setIsAdminMenuOpen(false)} />
                  {/* Popup - sidebar'ın sağına açılır */}
                  <div className="absolute bottom-0 left-full ml-3 z-[95] w-[220px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/60">
                      <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                        Admin Panelleri
                      </p>
                    </div>
                    <div className="p-2 space-y-1">
                      {canAccessTranslateAdminPanel() && (
                        <button
                          onClick={() => { navigate('/admin/translate'); setIsAdminMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[#02c2ff]/8 dark:hover:bg-[#02c2ff]/10 transition-colors duration-150 group text-left"
                        >
                          <div className="w-9 h-9 rounded-xl bg-[#02c2ff]/10 dark:bg-[#02c2ff]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#02c2ff]/20 transition-colors">
                            <svg className="w-4.5 h-4.5 w-[18px] h-[18px] text-[#02c2ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 group-hover:text-[#02c2ff] transition-colors">Çevirmen</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500">Admin Paneli</p>
                          </div>
                          <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                      {canAccessFRUpdateAdminPanel() && (
                        <button
                          onClick={() => { navigate('/admin/fr-update'); setIsAdminMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-violet-500/8 dark:hover:bg-violet-500/10 transition-colors duration-150 group text-left"
                        >
                          <div className="w-9 h-9 rounded-xl bg-violet-500/10 dark:bg-violet-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-500/20 transition-colors">
                            <svg className="w-[18px] h-[18px] text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 group-hover:text-violet-500 transition-colors">İçerik Güncelleme</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500">Admin Paneli</p>
                          </div>
                          <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Admin Button — nav menüsüyle aynı stil */}
              <button
                onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                className={`w-full h-12 flex items-center justify-start transition-all duration-200 group relative rounded-lg ${
                  isAdminMenuOpen
                    ? 'bg-[#02c2ff] text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                  <div className={`${isAdminMenuOpen ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={isAdminMenuOpen ? 2.2 : 1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                </div>
                <div className={`flex items-center justify-between flex-1 transition-all duration-300 overflow-hidden ${
                  isHovered ? 'opacity-100 w-auto' : 'opacity-0 w-0'
                }`}>
                  <span className="font-medium whitespace-nowrap text-base">Admin Paneli</span>
                  <svg className={`w-4 h-4 mr-3 flex-shrink-0 transition-transform duration-200 ${isAdminMenuOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>
          )}

          {/* User Profile */}
          <div>
            {/* Profile Button */}
            <button 
              onClick={handleOpenProfileModal}
              className="w-full h-12 flex items-center justify-start transition-all duration-200 group rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/50 dark:hover:bg-gray-700/50"
            >
              {/* Avatar - Always fixed position */}
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <div className="w-8 h-8 bg-gradient-to-r from-[#02c2ff] to-[#0199cc] rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              </div>
              
              {/* User Info - Only visible when expanded */}
              <div className={`flex-1 transition-all duration-300 overflow-hidden ${
                isHovered ? 'opacity-100 w-auto ml-2' : 'opacity-0 w-0'
              }`}>
                <div className="text-left">
                  <div className="text-gray-700 dark:text-gray-300 text-sm font-medium whitespace-nowrap">
                    {user?.name || 'User'}
                  </div>
                  <div className="text-gray-500 dark:text-gray-500 text-xs whitespace-nowrap truncate">
                    {user?.email}
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Profile Modal - Enhanced Version */}
      {isProfileModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[200] animate-fade-in">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl w-[600px] max-w-[90vw] shadow-2xl overflow-hidden transform transition-all duration-300 border border-gray-200 dark:border-gray-700">
            
            {/* Premium Header with Pattern */}
            <div className="relative px-8 pt-8 pb-6 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/20 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
              {/* Subtle Pattern Background */}
              <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_1px_1px,_#000_1px,_transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)]" style={{
                backgroundSize: '24px 24px'
              }}></div>
              
              {/* Accent Line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#02c2ff] to-transparent"></div>
              
              {/* Close Button */}
              <button
                onClick={() => setIsProfileModalOpen(false)}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 hover:bg-white/80 dark:hover:bg-gray-700/50 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 shadow-sm hover:shadow backdrop-blur-sm z-30"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* User Info Header */}
              <div className="relative flex items-start gap-5">
                {/* Premium Avatar with Ring */}
                <div className="relative group">
                  <div className="absolute inset-0 rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-300 bg-gradient-to-br from-[#02c2ff] to-[#0199cc] dark:to-[#0177aa]"></div>
                  <div className="relative w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg ring-4 transform transition-transform duration-200 group-hover:scale-105 bg-gradient-to-br from-[#02c2ff] to-[#0199cc] dark:to-[#0177aa] ring-white dark:ring-gray-800">
                    <span className="text-white text-3xl font-bold">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>

                {/* User Details with Better Typography */}
                <div className="flex-1 min-w-0 pt-1">
                  <h3 className="text-gray-900 dark:text-white text-2xl font-bold mb-1">
                    {user?.name || 'User'}
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 dark:bg-green-400 shadow-sm"></div>
                    <span className="text-green-600 dark:text-green-400 text-xs font-medium">
                      Aktif
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="px-8 pt-6 pb-6">
              {/* Premium Tabs */}
              <div className="flex gap-3 mb-6 p-1 rounded-xl border bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-900 dark:to-gray-800 border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveProfileTab('info')}
                  className={`group flex-1 px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-200 relative overflow-hidden ${
                    activeProfileTab === 'info'
                      ? 'bg-white dark:bg-gray-700 text-[#02c2ff] shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {activeProfileTab === 'info' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02c2ff]/10 to-transparent"></div>
                  )}
                  <div className="relative flex items-center justify-center gap-2">
                    <svg className={`w-4 h-4 transition-transform duration-200 ${
                      activeProfileTab === 'info' ? 'scale-110' : 'group-hover:scale-105'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Bilgilerim
                  </div>
                </button>
                <button
                  onClick={() => setActiveProfileTab('username')}
                  className={`group flex-1 px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-200 relative overflow-hidden ${
                    activeProfileTab === 'username'
                      ? 'bg-white dark:bg-gray-700 text-[#02c2ff] shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {activeProfileTab === 'username' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02c2ff]/10 to-transparent"></div>
                  )}
                  <div className="relative flex items-center justify-center gap-2">
                    <svg className={`w-4 h-4 transition-transform duration-200 ${
                      activeProfileTab === 'username' ? 'scale-110' : 'group-hover:scale-105'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Kullanıcı Adı
                  </div>
                </button>
                <button
                  onClick={() => setActiveProfileTab('password')}
                  className={`group flex-1 px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-200 relative overflow-hidden ${
                    activeProfileTab === 'password'
                      ? 'bg-white dark:bg-gray-700 text-[#02c2ff] shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {activeProfileTab === 'password' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02c2ff]/10 to-transparent"></div>
                  )}
                  <div className="relative flex items-center justify-center gap-2">
                    <svg className={`w-4 h-4 transition-transform duration-200 ${
                      activeProfileTab === 'password' ? 'scale-110' : 'group-hover:scale-105'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Şifre
                  </div>
                </button>
              </div>

              {/* Tab Content */}
              <div className="min-h-[300px]">
                {/* Profile Info Tab */}
                {activeProfileTab === 'info' && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="p-5 rounded-xl border-2 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-700 dark:to-gray-600 border-blue-100 dark:border-gray-600">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            Kullanıcı Adı
                          </label>
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">
                            {user?.name || 'Belirtilmemiş'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-5 rounded-xl border-2 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-700 dark:to-gray-600 border-purple-100 dark:border-gray-600">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            E-posta Adresi
                          </label>
                          <p className="text-lg font-semibold text-gray-900 dark:text-white break-all">
                            {user?.email || 'Belirtilmemiş'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-5 rounded-xl border-2 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 border-green-100 dark:border-gray-600">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            Roller
                          </label>
                          <p className="text-base font-semibold text-gray-900 dark:text-white leading-relaxed">
                            {formatAppRoles()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Change Username Tab */}
                {activeProfileTab === 'username' && (
                  <div className="space-y-5 animate-fade-in">
                    <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <div className="flex gap-3">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                          Kullanıcı adınızı değiştirmek için mevcut şifrenizi girmeniz gerekiyor. Değişiklik sonrası tekrar giriş yapmanız gerekecek.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Yeni Kullanıcı Adı <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-[#02c2ff] focus:ring-4 focus:ring-[#02c2ff]/20 outline-none transition-all"
                          placeholder="Yeni kullanıcı adınızı girin"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Mevcut Şifre <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <input
                          type={showCurrentPasswordForUsername ? "text" : "password"}
                          value={currentPasswordForUsername}
                          onChange={(e) => setCurrentPasswordForUsername(e.target.value)}
                          className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-[#02c2ff] focus:ring-4 focus:ring-[#02c2ff]/20 outline-none transition-all"
                          placeholder="Mevcut şifrenizi girin"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPasswordForUsername(!showCurrentPasswordForUsername)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showCurrentPasswordForUsername ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={handleChangeUsername}
                      className="w-full py-3.5 bg-gradient-to-r from-[#02c2ff] to-[#0199cc] hover:from-[#0199cc] hover:to-[#0177aa] text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Kullanıcı Adını Değiştir
                    </button>
                  </div>
                )}

                {/* Change Password Tab */}
                {activeProfileTab === 'password' && (
                  <div className="space-y-5 animate-fade-in">
                    <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <div className="flex gap-3">
                        <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          Şifrenizi değiştirdikten sonra güvenlik nedeniyle tekrar giriş yapmanız gerekecek. Yeni şifreniz en az 6 karakter olmalıdır.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Eski Şifre <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <input
                          type={showOldPassword ? "text" : "password"}
                          value={oldPassword}
                          onChange={(e) => setOldPassword(e.target.value)}
                          className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-[#02c2ff] focus:ring-4 focus:ring-[#02c2ff]/20 outline-none transition-all"
                          placeholder="Mevcut şifrenizi girin"
                        />
                        <button
                          type="button"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showOldPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Yeni Şifre <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </div>
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-[#02c2ff] focus:ring-4 focus:ring-[#02c2ff]/20 outline-none transition-all"
                          placeholder="Yeni şifrenizi girin (min. 6 karakter)"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showNewPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Yeni Şifre (Tekrar) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <input
                          type={showConfirmNewPassword ? "text" : "password"}
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-[#02c2ff] focus:ring-4 focus:ring-[#02c2ff]/20 outline-none transition-all"
                          placeholder="Yeni şifrenizi tekrar girin"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showConfirmNewPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={handleChangePassword}
                      className="w-full py-3.5 bg-gradient-to-r from-[#02c2ff] to-[#0199cc] hover:from-[#0199cc] hover:to-[#0177aa] text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Şifremi Değiştir
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Premium Footer Actions */}
            <div className="relative px-8 py-6 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-900 dark:to-gray-800 overflow-hidden">
              {/* Subtle Pattern */}
              <div className="absolute inset-0 opacity-[0.02] bg-[radial-gradient(circle_at_1px_1px,_#000_1px,_transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)]" style={{
                backgroundSize: '20px 20px'
              }}></div>
              
              <div className="relative flex justify-between items-center">
                <button
                  onClick={() => setIsProfileModalOpen(false)}
                  className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 border bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm hover:shadow"
                >
                  Kapat
                </button>
                {onLogout && (
                  <button
                    onClick={() => {
                      setIsProfileModalOpen(false);
                      onLogout();
                    }}
                    className="group relative px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center gap-2.5 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <svg className="w-5 h-5 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="relative z-10">Çıkış Yap</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Sidebar;
